import React, { useState, useEffect } from 'react';
import { FiUser, FiMail, FiPhone, FiLock, FiSave, FiShield, FiBell, FiSettings as FiSettingsIcon } from 'react-icons/fi';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const Settings = () => {
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    contactNumber: ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      setProfile({
        name: user.name || '',
        email: user.email || '',
        contactNumber: user.contactNumber || ''
      });
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      toast.success('Password changed successfully');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      toast.error('Failed to change password');
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <div className="settings-header">
        <div className="settings-header-content">
          <div className="settings-icon-wrapper">
            <FiSettingsIcon className="settings-main-icon" />
          </div>
          <div>
            <h1>Account Settings</h1>
            <p>Manage your profile, security, and preferences</p>
          </div>
        </div>
      </div>

      <div className="settings-layout">
        <div className="settings-sidebar">
          <button 
            className={`settings-tab ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            <FiUser className="tab-icon" />
            <span>Profile</span>
          </button>
          <button 
            className={`settings-tab ${activeTab === 'security' ? 'active' : ''}`}
            onClick={() => setActiveTab('security')}
          >
            <FiShield className="tab-icon" />
            <span>Security</span>
          </button>
          <button 
            className={`settings-tab ${activeTab === 'notifications' ? 'active' : ''}`}
            onClick={() => setActiveTab('notifications')}
          >
            <FiBell className="tab-icon" />
            <span>Notifications</span>
          </button>
        </div>

        <div className="settings-content">
          {activeTab === 'profile' && (
            <div className="settings-section">
              <div className="section-header">
                <h2>Profile Information</h2>
                <p>Update your personal details and contact information</p>
              </div>
              
              <form onSubmit={handleProfileUpdate} className="settings-form">
                <div className="form-row">
                  <div className="form-group-modern">
                    <label>
                      <FiUser className="input-icon" />
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={profile.name}
                      onChange={(e) => setProfile({...profile, name: e.target.value})}
                      placeholder="Enter your full name"
                      className="modern-input"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group-modern">
                    <label>
                      <FiMail className="input-icon" />
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={profile.email}
                      onChange={(e) => setProfile({...profile, email: e.target.value})}
                      placeholder="your@email.com"
                      className="modern-input"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group-modern">
                    <label>
                      <FiPhone className="input-icon" />
                      Contact Number
                    </label>
                    <input
                      type="tel"
                      value={profile.contactNumber}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        if (value.length <= 10) {
                          setProfile({...profile, contactNumber: value});
                        }
                      }}
                      placeholder="10-digit mobile number"
                      pattern="[0-9]{10}"
                      maxLength="10"
                      className="modern-input"
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn-save">
                    <FiSave />
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="settings-section">
              <div className="section-header">
                <h2>Security Settings</h2>
                <p>Manage your password and account security</p>
              </div>
              
              <form onSubmit={handlePasswordChange} className="settings-form">
                <div className="form-row">
                  <div className="form-group-modern">
                    <label>
                      <FiLock className="input-icon" />
                      Current Password
                    </label>
                    <input
                      type="password"
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                      placeholder="Enter your current password"
                      className="modern-input"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group-modern">
                    <label>
                      <FiLock className="input-icon" />
                      New Password
                    </label>
                    <input
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                      placeholder="Enter new password"
                      className="modern-input"
                    />
                    <small className="input-hint">Must be at least 6 characters long</small>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group-modern">
                    <label>
                      <FiLock className="input-icon" />
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                      placeholder="Confirm your new password"
                      className="modern-input"
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn-save">
                    <FiSave />
                    Update Password
                  </button>
                </div>
              </form>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className="settings-section">
              <div className="section-header">
                <h2>Notification Preferences</h2>
                <p>Choose how you want to receive notifications</p>
              </div>
              
              <div className="notification-settings">
                <div className="notification-item">
                  <div className="notification-info">
                    <h4>Order Notifications</h4>
                    <p>Get notified about new orders from your restaurants</p>
                  </div>
                  <label className="toggle-switch">
                    <input type="checkbox" defaultChecked />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="notification-item">
                  <div className="notification-info">
                    <h4>Sales Reports</h4>
                    <p>Receive daily and weekly sales summary reports</p>
                  </div>
                  <label className="toggle-switch">
                    <input type="checkbox" defaultChecked />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="notification-item">
                  <div className="notification-info">
                    <h4>System Updates</h4>
                    <p>Important updates about system changes and features</p>
                  </div>
                  <label className="toggle-switch">
                    <input type="checkbox" defaultChecked />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
